/**
 * fetchWithTimeout — fetch with an AbortController-driven timeout.
 *
 * Per Phase 4 P4-4 systematic-debugging: DevTools "Offline" mode (and
 * other offline conditions) cause a plain fetch to hang indefinitely
 * (no error, no response). Without an AbortController, the caller's
 * catch block never runs, shouldQueue never triggers, and the offline
 * queue is never written. This wrapper aborts the fetch after `timeoutMs`
 * and throws a network-style error that the shouldQueue helper matches
 * (the message contains "network" and "timed out").
 *
 * Used by src/lib/api/api-client.ts and src/lib/api/supabase-client.ts.
 */

const DEFAULT_TIMEOUT_MS = 10000;

export async function fetchWithTimeout(
  input: RequestInfo,
  init: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (err: any) {
    if (err?.name === "AbortError") {
      const e: any = new Error("Network request timed out");
      e.name = "AbortError";
      throw e;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}
